import { formatDate } from '@angular/common';
import { Workbook, WorkbookSheetRowCell } from '@progress/kendo-angular-excel-export';

/** One column of an exported sheet: its header and how a row fills its cell. */
export interface ExcelSheetColumn<T> {
  title: string;
  /** Column width in pixels. */
  width: number;
  cell: (row: T) => WorkbookSheetRowCell;
}

/** The date in a file name: 16-10-2026. */
export function fileDate(date: Date = new Date()): string {
  return formatDate(date, 'dd-MM-yyyy', 'en-US');
}

/**
 * A date cell that Excel shows as 16 Oct 2026 (or in `format`) - still a real date, so it sorts and filters as one.
 */
export function dateCell(value: string | Date | null | undefined, emptyText = '-', format = 'dd mmm yyyy'): WorkbookSheetRowCell {
  return value ? { value: new Date(value), format } : { value: emptyText };
}

/** A yes/no cell for a true/false field. */
export function yesNoCell(value: boolean | null | undefined): WorkbookSheetRowCell {
  return { value: value ? 'Yes' : 'No' };
}

/** An amount cell that keeps its currency symbol (₹999, ₹1,250.50) while staying a number Excel can add up. */
export function amountCell(value: number | null | undefined, currencySymbol: string): WorkbookSheetRowCell {
  if (value == null) return { value: '-' };
  const number = Number.isInteger(value) ? '#,##0' : '#,##0.00';
  return { value, format: `"${currencySymbol ?? ''}"${number}` };
}

/** Saves `rows` as a one-sheet .xlsx file with a bold header row. */
export async function saveExcelSheet<T>(fileName: string, columns: ExcelSheetColumn<T>[], rows: T[]): Promise<void> {
  const workbook = new Workbook({
    sheets: [{
      columns: columns.map(c => ({ width: c.width })),
      frozenRows: 1,
      rows: [
        { cells: columns.map(c => ({ value: c.title, bold: true, background: '#e6f0f0' })) },
        ...rows.map(row => ({ cells: columns.map(c => ({ verticalAlign: 'top' as const, ...c.cell(row) })) })),
      ],
    }],
  });

  const link = document.createElement('a');
  link.href = await workbook.toDataURL();
  link.download = fileName;
  link.click();
}
